import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * Bank Balance Discrepancy Check API
 * GET /api/bank/descuadre?bank_account_id=X&fecha_corte=YYYY-MM-DD
 *
 * Calculates the difference between the bank balance (from imported movements)
 * and the accounting balance (from accounting entries) at a given date.
 *
 * Returns: { saldoBanco, saldoContable, descuadre, detalle[] }
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'GET') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    const { bank_account_id, fecha_corte } = req.query;

    if (!bank_account_id) {
      return res.status(400).json({ success: false, error: 'bank_account_id es obligatorio.' });
    }

    if (!fecha_corte) {
      return res.status(400).json({ success: false, error: 'fecha_corte es obligatorio.' });
    }

    // ─── 1. Verify bank account and get initial balance ───
    const { data: account, error: accountError } = await supabaseAdmin
      .from('bank_accounts')
      .select('id, empresa_id, nombre, iban, saldo_inicial, saldo_actual, subcuenta_contable')
      .eq('id', bank_account_id as string)
      .maybeSingle();

    if (accountError || !account) {
      return res.status(404).json({
        success: false,
        error: 'Cuenta bancaria no encontrada.',
      });
    }

    // ─── 2. Calculate bank balance (saldo_inicial + sum of movements up to fecha_corte) ───
    const { data: movements, error: movError } = await supabaseAdmin
      .from('bank_movements')
      .select('id, fecha_operacion, concepto, importe, estado_conciliacion, tipo_movimiento')
      .eq('bank_account_id', bank_account_id as string)
      .lte('fecha_operacion', fecha_corte as string)
      .order('fecha_operacion', { ascending: true });

    if (movError) {
      console.error('[API bank/descuadre] Error fetching movements:', movError);
      return res.status(500).json({ success: false, error: movError.message });
    }

    const saldoBanco = (account.saldo_inicial || 0) +
      (movements || []).reduce((sum: number, mov: Record<string, unknown>) => sum + (Number(mov.importe) || 0), 0);

    // ─── 3. Calculate accounting balance ───
    // Try to get from accounting entries (asientos) linked to this bank account's subcuenta
    let saldoContable = account.saldo_inicial || 0;
    const detalleDescuadre: Array<{
      tipo: string;
      descripcion: string;
      importe: number;
      fecha?: string;
      movimiento_id?: string;
    }> = [];

    if (account.subcuenta_contable) {
      // Try to query datos_financieros or accounting entries
      const { data: asientos } = await supabaseAdmin
        .from('datos_financieros')
        .select('id, tipo, importe, fecha')
        .eq('empresa_id', account.empresa_id)
        .lte('fecha', fecha_corte as string)
        .or(`subcuenta.eq.${account.subcuenta_contable},cuenta.eq.${account.subcuenta_contable}`);

      if (asientos && asientos.length > 0) {
        saldoContable = (account.saldo_inicial || 0) +
          asientos.reduce((sum: number, a: Record<string, unknown>) => sum + (Number(a.importe) || 0), 0);
      }
    }

    // ─── 4. Identify reconciliation gaps ───
    // Movements in bank but not reconciled (no matching accounting entry)
    const pendingMovements = (movements || []).filter(
      (m: Record<string, unknown>) => m.estado_conciliacion === 'pendiente'
    );

    for (const mov of pendingMovements) {
      detalleDescuadre.push({
        tipo: 'movimiento_sin_conciliar',
        descripcion: `Movimiento bancario sin conciliar: ${mov.concepto || 'Sin concepto'}`,
        importe: Number(mov.importe) || 0,
        fecha: mov.fecha_operacion as string,
        movimiento_id: mov.id as string,
      });
    }

    // Partially reconciled movements
    const partialMovements = (movements || []).filter(
      (m: Record<string, unknown>) => m.estado_conciliacion === 'parcial'
    );

    for (const mov of partialMovements) {
      detalleDescuadre.push({
        tipo: 'movimiento_parcial',
        descripcion: `Movimiento parcialmente conciliado: ${mov.concepto || 'Sin concepto'}`,
        importe: Number(mov.importe) || 0,
        fecha: mov.fecha_operacion as string,
        movimiento_id: mov.id as string,
      });
    }

    // ─── 5. Calculate discrepancy ───
    const descuadre = Math.round((saldoBanco - saldoContable) * 100) / 100;

    // Summary statistics
    const totalMovimientos = movements?.length || 0;
    const conciliados = (movements || []).filter(
      (m: Record<string, unknown>) => m.estado_conciliacion === 'conciliado'
    ).length;

    return res.status(200).json({
      success: true,
      data: {
        cuenta: {
          id: account.id,
          nombre: account.nombre,
          iban: account.iban,
          subcuenta_contable: account.subcuenta_contable,
        },
        fecha_corte: fecha_corte as string,
        saldoBanco: Math.round(saldoBanco * 100) / 100,
        saldoContable: Math.round(saldoContable * 100) / 100,
        descuadre,
        hayDescuadre: Math.abs(descuadre) >= 0.01,
        resumen: {
          total_movimientos: totalMovimientos,
          conciliados,
          pendientes: pendingMovements.length,
          parciales: partialMovements.length,
          tasa_conciliacion: totalMovimientos > 0
            ? Math.round((conciliados / totalMovimientos) * 10000) / 100
            : 100,
        },
        detalle: detalleDescuadre,
      },
    });
  } catch (error: any) {
    console.error('[API bank/descuadre] Unexpected error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error inesperado al calcular descuadre.',
    });
  }
}

export default withAuth(handler);
